import React, { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from './supabase'
import { S, FONT_HEAD, FONT_BODY } from './styles'

// Shared button and chip shapes, matching Content, Events and My Wins. Gold is
// reserved for the single most important action on a screen; everything else is
// a quiet outline.
const GOLD_BTN = { background:'#fdbf00', color:'#412402', border:'none', borderRadius:'8px',
  padding:'7px 16px', fontSize:'12px', fontWeight:500, fontFamily:FONT_BODY, cursor:'pointer', whiteSpace:'nowrap' }
const QUIET_BTN = { background:'transparent', color:'#666', border:'0.5px solid #e8e8e8', borderRadius:'8px',
  padding:'7px 16px', fontSize:'12px', fontWeight:500, fontFamily:FONT_BODY, cursor:'pointer', whiteSpace:'nowrap' }
const PILL = (bg, fg) => ({ display:'inline-block', padding:'3px 8px', borderRadius:'12px', fontSize:'10px',
  fontWeight:600, fontFamily:FONT_BODY, whiteSpace:'nowrap', background:bg, color:fg })

const MAX_PHOTOS = 6
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB
const ALLOWED_EXTS = ['jpg', 'jpeg', 'png', 'webp']
const PHOTOS_BUCKET = 'open-house-photos'

function storagePathFromPublicUrl(url) {
  if (!url || typeof url !== 'string') return null
  const marker = `/${PHOTOS_BUCKET}/`
  const idx = url.indexOf(marker)
  if (idx === -1) return null
  return url.slice(idx + marker.length).split('?')[0]
}

function formatDateTime(iso) {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    return d.toLocaleString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    }).replace(',', ' ·').replace(/,\s/, ' · ')
  } catch { return iso }
}

function formatDateLong(iso) {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
  } catch { return iso }
}

function csvEscape(value) {
  if (value === null || value === undefined) return ''
  const s = String(value)
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

export default function OpenHouseAdmin({ session, profile, showToast, navigateTo, isMobile }) {
  const [tab, setTab] = useState('edit')
  const [targetAgent, setTargetAgent] = useState(null)

  // Readiness guard — EditTab/LeadsTab read profile.id; render nothing until
  // profile hydrates (App.js's session-effect normally settles before this
  // mounts, but the gate was removed for non-admin access so guard defensively).
  if (!profile) {
    return <div style={{textAlign:'center',padding:'60px',color:'#999',fontFamily:FONT_BODY}}>Loading…</div>
  }

  const isAdmin = profile.role === 'admin'

  // Admins without a target see the roster of every agent's open house. Picking
  // an agent (via Edit on a row or via "+ New for agent") sets targetAgent and
  // drops into the editor below. Non-admins skip this branch entirely.
  if (isAdmin && !targetAgent) {
    return (
      <AdminRoster
        session={session}
        profile={profile}
        showToast={showToast}
        onPickAgent={setTargetAgent}
      />
    )
  }

  // Editor mode — either a regular agent (targetAgent=null → owner=profile in
  // EditTab), or an admin who picked an agent (targetAgent set → owner=targetAgent).
  const editingFor = targetAgent

  return (
    <div>
        <div style={{padding:'4px 0 20px'}}>
          {editingFor ? (
            <>
              <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'14px',flexWrap:'wrap'}}>
                <button onClick={() => setTargetAgent(null)} style={{
                  background:'transparent',
                  border:'0.5px solid #e8e8e8',
                  borderRadius:'8px',
                  padding:'6px 12px',
                  fontSize:'12px',
                  fontWeight:500,
                  fontFamily:FONT_BODY,
                  color:'#666',
                  cursor:'pointer',
                }}>← Back to roster</button>
              </div>
              <div style={{fontSize:'16px',fontWeight:500,lineHeight:1.3,color:'#1d2e42',marginBottom:'10px',fontFamily:FONT_BODY}}>Editing for <span style={{color:'#147efb'}}>{editingFor.full_name || editingFor.email}</span></div>
              <div style={{fontSize:'14px',color:'#666',fontFamily:FONT_BODY,maxWidth:'640px'}}>You are managing this agent's open house. The "updated by" stamp will record you as the editor, but the row stays owned by the agent and continues to publish at <code style={{fontFamily:'monospace'}}>/openhouse/&lt;their slug&gt;</code>.</div>
            </>
          ) : (
            <div style={{fontSize:'14px',color:'#666',fontFamily:FONT_BODY,maxWidth:'640px'}}>The active row drives what visitors see at /openhouse. "Start New Open House" archives the current row and opens a blank one for next weekend's property.</div>
          )}
        </div>

        {/* Same compact segmented pill the Events title row uses. It stays in the
            stage rather than moving to the shell's summary: both `tab` and
            `targetAgent` live here, and OpenHouseView would need both lifted into
            App.js to know whether to render a tab row at all. */}
        <div style={{display:'inline-flex',border:'0.5px solid #e8e8e8',borderRadius:'16px',overflow:'hidden',marginBottom:'20px'}}>
          {[['edit','Edit open house'],['leads','Leads']].map(([id,label]) => (
            <button key={id} onClick={()=>setTab(id)}
              style={{border:'none',padding:'5px 14px',fontSize:'12px',fontFamily:FONT_BODY,fontWeight:600,
                whiteSpace:'nowrap',cursor:'pointer',transition:'all .15s',
                background: tab===id ? '#f4f4f2' : 'transparent',
                color: tab===id ? '#1d2e42' : '#666'}}>
              {label}
            </button>
          ))}
        </div>

        {/* key forces EditTab to remount when admin switches between agents — the
            mount-only useEffect is what re-runs the loader; without a key, the
            same EditTab instance would keep stale form state from agent A when
            admin picked agent B. */}
        {tab === 'edit' && <EditTab key={editingFor?.id || 'self'} session={session} profile={profile} showToast={showToast} targetAgent={targetAgent} isMobile={isMobile} />}
        {tab === 'leads' && <LeadsTab session={session} profile={profile} showToast={showToast} targetAgent={targetAgent} />}
    </div>
  )
}

const MAX_DAYS = 3
const EMPTY_DAY = { date: '', start_time: '', end_time: '' }

function normalizeScheduleDay(day) {
  if (!day || typeof day !== 'object') return null
  const date = typeof day.date === 'string' ? day.date : ''
  const start = typeof day.start_time === 'string' ? day.start_time.slice(0, 5) : ''
  const end = typeof day.end_time === 'string' ? day.end_time.slice(0, 5) : ''
  return { date, start_time: start, end_time: end }
}

function slugify(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function EditTab({ session, profile, showToast, targetAgent, isMobile }) {
  // owner = whose open house this editor is acting on. Defaults to the logged-in
  // user (the existing "agent edits own" flow). When an admin opens an agent's
  // editor via the roster, targetAgent is that agent's profile, separating
  // OWNER (owner.id, used for agent_id / slug / brokerage check) from ACTOR
  // (session.user.id, used for updated_by audit stamps).
  const owner = targetAgent || profile
  const [loading, setLoading] = useState(true)
  const [oh, setOh] = useState(null)               // current active row or null
  const [address, setAddress] = useState('')
  const [scheduleDays, setScheduleDays] = useState([{ ...EMPTY_DAY }])
  const [photos, setPhotos] = useState([])         // array of public URLs
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [uploading, setUploading] = useState(false)
  const [replaceConfirm, setReplaceConfirm] = useState(false)
  const [replacing, setReplacing] = useState(false)
  const [updatedByName, setUpdatedByName] = useState('')
  const fileInputRef = useRef(null)

  function updateDayField(idx, field, value) {
    setScheduleDays(prev => prev.map((d, i) => i === idx ? { ...d, [field]: value } : d))
  }
  function addDay() {
    setScheduleDays(prev => prev.length >= MAX_DAYS ? prev : [...prev, { ...EMPTY_DAY }])
  }
  function removeDay(idx) {
    if (idx === 0) return
    setScheduleDays(prev => prev.filter((_, i) => i !== idx))
  }

  const loadActiveRow = useCallback(async () => {
    setLoading(true)
    // Load the most recently touched row for the OWNER (the agent whose open
    // house we're editing), active OR draft. Drafts (is_active=false) stay
    // editable here even though the public /openhouse/<slug> page can't see them.
    const { data, error: loadErr } = await supabase
      .from('open_house')
      .select('*')
      .eq('agent_id', owner.id)
      .order('updated_at', { ascending: false })
      .limit(1)
    if (loadErr) {
      console.error('[open_house load]', loadErr)
      showToast('Could not load open house: ' + loadErr.message)
      setLoading(false)
      return
    }
    const row = data && data[0]
    if (!row) {
      setOh(null)
      setAddress('')
      setScheduleDays([{ ...EMPTY_DAY }])
      setPhotos([])
      setUpdatedByName('')
      setLoading(false)
      return
    }
    setOh(row)
    setAddress(row.address || '')
    // Prefer the schedule JSONB array; fall back to legacy event_date/start_time/end_time
    let days = Array.isArray(row.schedule) ? row.schedule.map(normalizeScheduleDay).filter(Boolean) : []
    if (days.length === 0) {
      days = [{
        date: row.event_date || '',
        start_time: (row.start_time || '').slice(0, 5),
        end_time: (row.end_time || '').slice(0, 5),
      }]
    }
    // Cap at MAX_DAYS in case the DB ever holds more (defensive)
    setScheduleDays(days.slice(0, MAX_DAYS))
    setPhotos(Array.isArray(row.photos) ? row.photos.filter(u => typeof u === 'string' && u.trim()) : [])
    if (row.updated_by) {
      const { data: p } = await supabase.from('profiles').select('full_name, email').eq('id', row.updated_by).single()
      setUpdatedByName(p?.full_name || p?.email || '')
    } else {
      setUpdatedByName('')
    }
    setLoading(false)
  }, [showToast, owner.id])

  // Fire on mount only. Depending on `loadActiveRow` here would re-fire any
  // time its `showToast` dep changes ref — and App.js's showToast is a fresh
  // function on every Dashboard render. That cascade was wiping the admin's
  // unsaved address/schedule edits whenever a photo upload triggered a toast.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadActiveRow() }, [])

  function validate() {
    if (!address.trim()) return 'Address is required'
    if (scheduleDays.length === 0) return 'At least one day is required'
    for (let i = 0; i < scheduleDays.length; i++) {
      const d = scheduleDays[i]
      const dayLabel = `Day ${i + 1}`
      if (!d.date) return `${dayLabel}: Event date is required`
      if (!d.start_time) return `${dayLabel}: Start time is required`
      if (!d.end_time) return `${dayLabel}: End time is required`
      if (d.end_time <= d.start_time) return `${dayLabel}: End time must be after start time`
    }
    return ''
  }

  async function handleSave() {
    const v = validate()
    if (v) { setError(v); return }
    // Compliance guard: the public landing's RPC fails safe on null brokerage,
    // and the brokerage modal (App.js) should make this rare — but guard so
    // we don't create an open house that can't render publicly. Check the
    // OWNER's brokerage (not the actor's) — an admin acting for an agent
    // shouldn't be able to create an open house the agent can't publish.
    if (!oh && !(owner?.brokerage && String(owner.brokerage).trim())) {
      const actingForOther = targetAgent && targetAgent.id !== profile.id
      showToast(actingForOther
        ? `${owner.full_name || 'This agent'} has no brokerage set on their profile — set it before creating their open house.`
        : 'Set your brokerage on your profile before creating an open house.')
      return
    }
    setError('')
    setSaving(true)
    try {
      // Normalize and mirror day 1 into legacy columns for backward compatibility
      const schedule = scheduleDays.map(d => ({
        date: d.date,
        start_time: d.start_time,
        end_time: d.end_time,
      }))
      const firstDay = schedule[0]
      const payload = {
        address: address.trim(),
        schedule,
        event_date: firstDay.date,
        start_time: firstDay.start_time,
        end_time: firstDay.end_time,
        is_active: true,
        updated_at: new Date().toISOString(),
        updated_by: session.user.id,
      }
      if (oh) {
        // agent_id / slug / source_tag are immutable in 3a — never written on update.
        const { error: upErr } = await supabase.from('open_house').update(payload).eq('id', oh.id)
        if (upErr) throw upErr
        showToast('Open house updated')
      } else {
        // First open house for the OWNER. Auto-generate a stable slug from
        // their full name; fall back to a deterministic suffix if it's taken.
        const base = slugify(owner.full_name) || 'agent'
        let slug = base
        const { data: existing } = await supabase
          .from('open_house')
          .select('slug')
          .eq('slug', base)
          .limit(1)
        if (existing && existing.length > 0) {
          const idSuffix = String(owner.id || '').replace(/-/g, '').slice(0, 6)
          slug = `${base}-${idSuffix}`
        }
        payload.agent_id = owner.id
        payload.slug = slug
        payload.source_tag = 'openhouse-' + slug
        payload.photos = []
        const { error: insErr } = await supabase.from('open_house').insert(payload)
        if (insErr) throw insErr
        showToast('Open house created')
      }
      await loadActiveRow()
    } catch (e) {
      console.error('[open_house save]', e)
      showToast('Save failed: ' + (e.message || 'unknown'))
    } finally {
      setSaving(false)
    }
  }

  async function handleReplace() {
    if (!oh) { setReplaceConfirm(false); return }
    setReplacing(true)
    try {
      const { error: upErr } = await supabase.from('open_house').update({
        is_active: false,
        updated_at: new Date().toISOString(),
        updated_by: session.user.id,
      }).eq('id', oh.id)
      if (upErr) throw upErr
      const today = new Date().toISOString().slice(0, 10)
      const newSchedule = [{ date: today, start_time: '11:00', end_time: '14:00' }]
      const { error: insErr } = await supabase.from('open_house').insert({
        address: '',
        schedule: newSchedule,
        event_date: today,
        start_time: '11:00',
        end_time: '14:00',
        photos: [],
        is_active: false,
        agent_id: oh.agent_id,
        slug: oh.slug,
        source_tag: oh.source_tag,
        updated_at: new Date().toISOString(),
        updated_by: session.user.id,
      })
      if (insErr) throw insErr
      setReplaceConfirm(false)
      showToast('Open house taken offline. Fill in the new details and save to publish.')
      await loadActiveRow()
      if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (e) {
      console.error('[open_house replace]', e)
      showToast('Replace failed: ' + (e.message || 'unknown'))
    } finally {
      setReplacing(false)
    }
  }

  async function persistPhotosArray(nextPhotos) {
    if (!oh) return false
    const { error: upErr } = await supabase.from('open_house').update({
      photos: nextPhotos,
      updated_at: new Date().toISOString(),
      updated_by: session.user.id,
    }).eq('id', oh.id)
    if (upErr) {
      console.error('[photos persist]', upErr)
      showToast('Photo update failed: ' + upErr.message)
      return false
    }
    setPhotos(nextPhotos)
    return true
  }

  async function uploadPhotos(filesList) {
    if (!oh) { showToast('Save the open house details first, then add photos.'); return }
    const files = Array.from(filesList || [])
    if (files.length === 0) return
    const room = MAX_PHOTOS - photos.length
    if (room <= 0) { showToast(`Maximum ${MAX_PHOTOS} photos. Delete one to add another.`); return }
    const accepted = []
    const rejected = []
    for (const f of files.slice(0, room)) {
      const ext = (f.name.split('.').pop() || '').toLowerCase()
      if (!ALLOWED_EXTS.includes(ext)) { rejected.push(`${f.name}: must be jpg/png/webp`); continue }
      if (f.size > MAX_FILE_SIZE) { rejected.push(`${f.name}: exceeds 10MB`); continue }
      accepted.push({ file: f, ext })
    }
    if (rejected.length) showToast(rejected[0] + (rejected.length > 1 ? ` (+${rejected.length-1} more)` : ''))
    if (accepted.length === 0) return

    setUploading(true)
    const uploadedUrls = []
    try {
      for (const { file, ext } of accepted) {
        const filename = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`
        const path = `${oh.id}/${filename}`
        const { error: upErr } = await supabase.storage.from(PHOTOS_BUCKET).upload(path, file, {
          upsert: false,
          contentType: file.type || `image/${ext === 'jpg' ? 'jpeg' : ext}`,
        })
        if (upErr) { showToast(`Upload failed: ${upErr.message}`); continue }
        const { data: urlData } = supabase.storage.from(PHOTOS_BUCKET).getPublicUrl(path)
        if (urlData?.publicUrl) uploadedUrls.push(urlData.publicUrl)
      }
      if (uploadedUrls.length > 0) {
        const next = [...photos, ...uploadedUrls]
        const ok = await persistPhotosArray(next)
        if (ok) showToast(`${uploadedUrls.length} photo${uploadedUrls.length > 1 ? 's' : ''} added`)
      }
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  async function deletePhoto(idx) {
    const url = photos[idx]
    if (!url) return
    const path = storagePathFromPublicUrl(url)
    if (path) {
      const { error: rmErr } = await supabase.storage.from(PHOTOS_BUCKET).remove([path])
      if (rmErr) console.warn('[storage remove]', rmErr)
    }
    const next = photos.filter((_, i) => i !== idx)
    await persistPhotosArray(next)
  }

  async function movePhoto(idx, direction) {
    const target = idx + direction
    if (target < 0 || target >= photos.length) return
    const next = [...photos]
    const [item] = next.splice(idx, 1)
    next.splice(target, 0, item)
    await persistPhotosArray(next)
  }

  function handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    if (uploading) return
    uploadPhotos(e.dataTransfer?.files)
  }

  if (loading) return <div style={{textAlign:'center',padding:'60px',color:'#999',fontFamily:FONT_BODY}}>Loading…</div>

  const atCapacity = photos.length >= MAX_PHOTOS
  const hasRow = !!oh

  return (
    <div style={{paddingBottom:'80px'}}>
      <div style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'20px',marginBottom:'20px'}}>
        <div style={{fontSize:'16px',fontWeight:500,color:'#1d2e42',fontFamily:FONT_BODY,marginBottom:'16px'}}>{hasRow ? 'Edit open house' : 'Create first open house'}</div>
        {hasRow && oh.is_active === false && (
          <div style={{background:'rgba(253,191,0,0.12)',border:'1px solid rgba(253,191,0,0.5)',borderLeft:'4px solid #fdbf00',borderRadius:'6px',padding:'14px 16px',marginBottom:'18px',display:'flex',alignItems:'flex-start',gap:'10px'}}>
            <div style={{fontSize:'18px',lineHeight:1,marginTop:'2px'}}>⚠️</div>
            <div>
              <div style={{fontSize:'12px',fontWeight:700,letterSpacing:'1.5px',color:'#8b6914',fontFamily:FONT_HEAD,marginBottom:'4px'}}>DRAFT — NOT YET PUBLISHED</div>
              <div style={{fontSize:'13px',color:'#5a4509',lineHeight:1.5,fontFamily:FONT_BODY}}>This open house is not visible on the public page. Fill in the details and click <strong>Save Changes</strong> to publish.</div>
            </div>
          </div>
        )}
        {error && <div style={S.errorBox}>{error}</div>}

        <div style={S.field}>
          <label style={S.label}>ADDRESS *</label>
          <input value={address} onChange={e=>setAddress(e.target.value)} style={S.input} placeholder="1234 E Camelback Rd, Phoenix, AZ 85014" />
        </div>

        <div style={{marginBottom:'16px'}}>
          <label style={{...S.label,marginBottom:'10px'}}>SCHEDULE ({scheduleDays.length}/{MAX_DAYS} {scheduleDays.length === 1 ? 'day' : 'days'})</label>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {scheduleDays.map((day, idx) => (
              <div key={idx} style={{background:'#f4f4f2',border:'1px solid #e8e8e8',borderRadius:'8px',padding:'12px 14px'}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'10px'}}>
                  <span style={{fontSize:'11px',fontWeight:700,letterSpacing:'1.5px',color:'#1d2e42',fontFamily:FONT_HEAD}}>DAY {idx + 1}</span>
                  {idx > 0 && (
                    <button
                      type="button"
                      onClick={()=>removeDay(idx)}
                      style={{background:'transparent',border:'1px solid rgba(200,16,46,0.3)',color:'#C8102E',borderRadius:'6px',padding:'4px 10px',fontSize:'11px',fontWeight:600,cursor:'pointer',fontFamily:FONT_HEAD,letterSpacing:'0.5px'}}>
                      Remove
                    </button>
                  )}
                </div>
                <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr 1fr',gap:'10px'}}>
                  <div>
                    <label style={{...S.label,marginBottom:'4px'}}>EVENT DATE *</label>
                    <input type="date" value={day.date} onChange={e=>updateDayField(idx, 'date', e.target.value)} style={S.input} />
                  </div>
                  <div>
                    <label style={{...S.label,marginBottom:'4px'}}>START TIME *</label>
                    <input type="time" value={day.start_time} onChange={e=>updateDayField(idx, 'start_time', e.target.value)} style={S.input} />
                  </div>
                  <div>
                    <label style={{...S.label,marginBottom:'4px'}}>END TIME *</label>
                    <input type="time" value={day.end_time} onChange={e=>updateDayField(idx, 'end_time', e.target.value)} style={S.input} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          {scheduleDays.length < MAX_DAYS && (
            <button
              type="button"
              onClick={addDay}
              style={{...QUIET_BTN,marginTop:'12px'}}>
              + Add another day
            </button>
          )}
        </div>

        <div style={{marginTop:'24px'}}>
          <label style={{...S.label,marginBottom:'10px'}}>PHOTOS ({photos.length}/{MAX_PHOTOS})</label>

          {!hasRow && (
            <div style={{background:'rgba(253,191,0,0.1)',border:'1px solid rgba(253,191,0,0.3)',borderRadius:'6px',padding:'12px 14px',fontSize:'13px',color:'#8b6914',marginBottom:'12px',fontFamily:FONT_BODY}}>
              Save the open house details first, then come back to add photos.
            </div>
          )}

          {photos.length > 0 && (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:'12px',marginBottom:'16px'}}>
              {photos.map((url, i) => (
                <div key={url + i} style={{position:'relative',aspectRatio:'4/3',borderRadius:'8px',overflow:'hidden',background:'#f4f4f2',border:'1px solid #e8e8e8'}}>
                  <img src={url} alt={`Open house ${i+1}`} style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />
                  <div style={{position:'absolute',top:'6px',left:'6px',background:'rgba(0,0,0,0.6)',color:'white',padding:'2px 8px',borderRadius:'4px',fontSize:'10px',fontWeight:700,fontFamily:FONT_HEAD,letterSpacing:'0.5px'}}>{i+1}</div>
                  <button onClick={()=>deletePhoto(i)} style={S.removeImgBtn} aria-label={`Delete photo ${i+1}`}>×</button>
                  <div style={{position:'absolute',bottom:'6px',right:'6px',display:'flex',gap:'4px'}}>
                    <button onClick={()=>movePhoto(i, -1)} disabled={i === 0} style={{width:'26px',height:'26px',borderRadius:'4px',background:'rgba(0,0,0,0.6)',color:'white',border:'none',cursor:i===0?'not-allowed':'pointer',fontSize:'14px',opacity:i===0?0.4:1,display:'flex',alignItems:'center',justifyContent:'center'}} aria-label="Move left">←</button>
                    <button onClick={()=>movePhoto(i, 1)} disabled={i === photos.length - 1} style={{width:'26px',height:'26px',borderRadius:'4px',background:'rgba(0,0,0,0.6)',color:'white',border:'none',cursor:i===photos.length-1?'not-allowed':'pointer',fontSize:'14px',opacity:i===photos.length-1?0.4:1,display:'flex',alignItems:'center',justifyContent:'center'}} aria-label="Move right">→</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!atCapacity && hasRow && (
            <div
              onDragOver={e=>{e.preventDefault();e.stopPropagation()}}
              onDrop={handleDrop}
              style={{...S.fileDropZone,padding:'32px',opacity:uploading?0.6:1,cursor:uploading?'wait':'pointer'}}
              onClick={()=>!uploading && fileInputRef.current?.click()}>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple style={{display:'none'}} onChange={e=>uploadPhotos(e.target.files)} />
              <div style={{fontSize:'14px',color:'#666',marginBottom:'4px',fontFamily:FONT_BODY,fontWeight:600}}>
                {uploading ? 'Uploading…' : 'Drag photos here or click to upload'}
              </div>
              <div style={{fontSize:'12px',color:'#999',fontFamily:FONT_BODY}}>JPG, PNG, or WebP · max 10MB each · up to {MAX_PHOTOS} total</div>
            </div>
          )}

          {atCapacity && (
            <div style={{background:'#f4f4f2',border:'1px solid #e8e8e8',borderRadius:'6px',padding:'14px',fontSize:'13px',color:'#666',textAlign:'center',fontFamily:FONT_BODY}}>
              Maximum {MAX_PHOTOS} photos. Delete one to add another.
            </div>
          )}
        </div>

        <div style={{display:'flex',gap:'10px',marginTop:'28px'}}>
          <button onClick={handleSave} disabled={saving} style={{...GOLD_BTN,opacity:saving?0.6:1,cursor:saving?'wait':'pointer'}}>{saving ? 'Saving…' : 'Save changes'}</button>
        </div>

        {hasRow && oh.updated_at && (
          <div style={{marginTop:'18px',fontSize:'12px',color:'#999',fontFamily:FONT_BODY}}>
            Last updated: {formatDateLong(oh.updated_at)}{updatedByName ? ` by ${updatedByName}` : ''}
          </div>
        )}
      </div>

      {hasRow && (
        <div style={{background:'white',border:'0.5px solid #e8e8e8',borderLeft:'4px solid #fdbf00',borderRadius:'12px',padding:'18px 20px'}}>
          <div style={{fontSize:'10px',fontWeight:700,color:'#999',fontFamily:FONT_HEAD,letterSpacing:'1.5px',marginBottom:'8px'}}>NEXT WEEKEND</div>
          <div style={{fontSize:'13px',color:'#666',marginBottom:'14px',fontFamily:FONT_BODY,maxWidth:'620px',lineHeight:1.5}}>
            This will take the current open house <strong>OFFLINE</strong> and start a new draft. The public page will show <em>"No open house this week"</em> until you fill in the new details and save. The old row stays in the database for history.
          </div>
          {!replaceConfirm ? (
            <button onClick={()=>setReplaceConfirm(true)} style={QUIET_BTN}>Start new open house</button>
          ) : (
            <div style={{display:'flex',gap:'10px',alignItems:'center',flexWrap:'wrap'}}>
              <span style={{fontSize:'13px',color:'#C8102E',fontFamily:FONT_BODY}}>Take current open house OFFLINE and start a new draft?</span>
              <button onClick={handleReplace} disabled={replacing} style={{...GOLD_BTN,padding:'6px 14px',opacity:replacing?0.6:1,cursor:replacing?'wait':'pointer'}}>{replacing ? 'Working…' : 'Yes, take offline'}</button>
              <button onClick={()=>setReplaceConfirm(false)} disabled={replacing} style={{...QUIET_BTN,padding:'6px 14px'}}>Cancel</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function LeadsTab({ session, profile, showToast, targetAgent }) {
  // targetAgent is plumbed for future per-agent lead filtering; current pass
  // relies on RLS to scope rows (admins see all, agents see own).
  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(true)
  const [sendingIds, setSendingIds] = useState(() => new Set())

  const loadLeads = useCallback(async () => {
    const { data, error } = await supabase
      .from('open_house_leads')
      .select('*')
      .order('created_at', { ascending: false })
    if (error) {
      console.error('[open_house_leads load]', error)
      showToast('Could not load leads: ' + error.message)
      setLeads([])
    } else {
      setLeads(data || [])
    }
  }, [showToast])

  // Fire on mount only — see note on the matching pattern in EditTab. Including
  // loadLeads in deps would re-fire whenever showToast's identity changes (which
  // happens on every parent re-render).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setLoading(true); loadLeads().finally(() => setLoading(false)) }, [])

  async function sendToFub(leadId) {
    if (sendingIds.has(leadId)) return
    setSendingIds(prev => { const next = new Set(prev); next.add(leadId); return next })
    try {
      const { data, error } = await supabase.functions.invoke('send-openhouse-lead-to-fub', {
        body: { lead_id: leadId },
      })
      if (error) {
        showToast('FUB push failed: ' + (error.message || 'unknown'))
        return
      }
      if (data?.error) {
        showToast('FUB push failed: ' + data.error)
        return
      }
      if (data?.skipped) showToast('Already sent to FUB')
      else if (data?.warning) showToast('Sent to FUB (warning: ' + data.warning + ')')
      else showToast('Sent to FUB')
      await loadLeads()
    } catch (e) {
      console.error('[fub push] threw:', e)
      showToast('FUB push failed: ' + (e?.message || String(e)))
    } finally {
      setSendingIds(prev => { const next = new Set(prev); next.delete(leadId); return next })
    }
  }

  function exportCsv() {
    const header = ['Submitted', 'Name', 'Phone', 'Email', 'Property Address', 'Source', 'Sent to FUB']
    const rows = leads.map(l => [
      l.created_at ? new Date(l.created_at).toISOString() : '',
      l.name || '',
      l.phone || '',
      l.email || '',
      l.property_address || '',
      l.source || '',
      l.sent_to_fub ? 'Yes' : 'No',
    ])
    const csv = [header, ...rows].map(r => r.map(csvEscape).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `open-house-leads-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  if (loading) return <div style={{textAlign:'center',padding:'60px',color:'#999',fontFamily:FONT_BODY}}>Loading leads…</div>

  return (
    <div style={{paddingBottom:'60px'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px',gap:'12px',flexWrap:'wrap'}}>
        <div style={{fontSize:'13px',color:'#999',fontFamily:FONT_BODY}}>{leads.length} {leads.length === 1 ? 'lead' : 'leads'} total</div>
        <button onClick={exportCsv} disabled={leads.length === 0} style={{...QUIET_BTN,cursor:leads.length===0?'not-allowed':'pointer',opacity:leads.length===0?0.5:1}}>Export CSV</button>
      </div>

      {leads.length === 0 ? (
        <div style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px',padding:'48px 20px',textAlign:'center',color:'#999',fontSize:'12.5px',fontFamily:FONT_BODY}}>
          No leads yet. Once visitors submit the form on /openhouse, they'll appear here.
        </div>
      ) : (
        <div style={{background:'white',border:'0.5px solid #e8e8e8',borderRadius:'12px',overflow:'hidden'}}>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px',fontFamily:FONT_BODY,minWidth:'780px'}}>
              <thead>
                <tr style={{background:'#f4f4f2',textAlign:'left'}}>
                  {['SUBMITTED','NAME','PHONE','EMAIL','PROPERTY ADDRESS','STATUS'].map(h => (
                    <th key={h} style={{padding:'10px 16px',fontFamily:FONT_BODY,fontWeight:600,letterSpacing:'1px',fontSize:'10px',color:'#666'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.map(l => (
                  <tr key={l.id} style={{borderBottom:'1px solid #e8e8e8'}}>
                    <td style={{padding:'12px 16px',color:'#666',whiteSpace:'nowrap'}}>{formatDateTime(l.created_at)}</td>
                    <td style={{padding:'12px 16px',fontWeight:600,color:'#666'}}>{l.name || <span style={{color:'#999'}}>—</span>}</td>
                    <td style={{padding:'12px 16px',color:'#666'}}>
                      {l.phone ? <a href={`tel:${l.phone}`} style={{color:'#147efb',textDecoration:'none'}}>{l.phone}</a> : <span style={{color:'#999'}}>—</span>}
                    </td>
                    <td style={{padding:'12px 16px',color:'#666'}}>
                      {l.email ? <a href={`mailto:${l.email}`} style={{color:'#147efb',textDecoration:'none'}}>{l.email}</a> : <span style={{color:'#999'}}>—</span>}
                    </td>
                    <td style={{padding:'12px 16px',color:'#666',maxWidth:'260px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{l.property_address || <span style={{color:'#999'}}>—</span>}</td>
                    <td style={{padding:'12px 16px'}}>
                      {l.sent_to_fub ? (
                        <span style={PILL('#EAF3DE','#27500A')}>Sent</span>
                      ) : (
                        <div style={{display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
                          <span style={PILL('#FAEEDA','#633806')}>Pending</span>
                          <button
                            onClick={()=>sendToFub(l.id)}
                            disabled={sendingIds.has(l.id)}
                            style={{background:'none',border:'none',padding:0,color:'#147efb',fontSize:'11px',fontWeight:600,fontFamily:FONT_BODY,cursor:sendingIds.has(l.id)?'wait':'pointer',opacity:sendingIds.has(l.id)?0.6:1}}>
                            {sendingIds.has(l.id) ? 'Sending…' : 'Send to FUB'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Admin-only roster: one row per agent (their latest open house), plus a
// "+ New for agent" picker that drops the admin into EditTab with the chosen
// agent as the target. Mirrors FunnelsAdmin's list + picker pattern.
// ────────────────────────────────────────────────────────────────────────────

const ROSTER_COLS = '1.2fr 1.2fr 1.4fr 0.7fr 1.2fr 0.9fr 0.7fr'
// Below this the seven columns stop being readable; the card scrolls instead.
const ROSTER_MIN_WIDTH = '860px'

function AdminRoster({ session, profile, showToast, onPickAgent }) {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])          // deduped: one row per agent (latest)
  const [agentsById, setAgentsById] = useState({})
  const [agents, setAgents] = useState([])      // sorted, selectable (has brokerage) first
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerAgentId, setPickerAgentId] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [ohRes, agentsRes] = await Promise.all([
        supabase.from('open_house').select('*').order('updated_at', { ascending: false }),
        supabase.from('profiles').select('id, full_name, email, role, brokerage').in('role', ['agent', 'admin']).order('full_name'),
      ])
      if (ohRes.error) {
        console.error('[open_house roster]', ohRes.error)
        showToast('Could not load open houses: ' + ohRes.error.message)
      }
      if (agentsRes.error) {
        console.error('[agents roster]', agentsRes.error)
        showToast('Could not load agents: ' + agentsRes.error.message)
      }

      const rawAgents = agentsRes.data || []
      const byId = {}
      rawAgents.forEach(a => { byId[a.id] = a })

      // Sort: selectable agents (have brokerage) first, then disabled, alphabetical within each.
      const sortedAgents = [...rawAgents].sort((a, b) => {
        const aHas = !!(a.brokerage && String(a.brokerage).trim())
        const bHas = !!(b.brokerage && String(b.brokerage).trim())
        if (aHas !== bHas) return aHas ? -1 : 1
        return String(a.full_name || '').localeCompare(String(b.full_name || ''))
      })

      // Dedupe open_house rows to one-per-agent (the latest, since data is sorted updated_at desc).
      const seen = new Set()
      const deduped = []
      for (const r of (ohRes.data || [])) {
        if (!r.agent_id || seen.has(r.agent_id)) continue
        seen.add(r.agent_id)
        deduped.push(r)
      }

      setRows(deduped)
      setAgentsById(byId)
      setAgents(sortedAgents)
    } finally {
      setLoading(false)
    }
  }, [showToast])

  // Fire on mount only — same pattern as EditTab/LeadsTab. Including `load` in
  // deps would re-fire whenever showToast's identity changes (every parent render).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [])

  function handlePick() {
    const agent = agentsById[pickerAgentId]
    if (!agent) return
    setPickerOpen(false)
    setPickerAgentId('')
    onPickAgent(agent)
  }

  return (
    <div>
        <div style={{padding:'4px 0 20px'}}>
          <div style={{fontSize:'14px',color:'#666',fontFamily:FONT_BODY,maxWidth:'640px'}}>One row per agent — their latest open house, active or draft. Click Edit to manage on their behalf; "+ New for agent" to start one for someone who doesn't have one yet.</div>
        </div>

        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px',flexWrap:'wrap',marginBottom:'16px'}}>
          <div style={{fontSize:'12px',color:'#666',fontFamily:FONT_BODY}}>
            {loading ? 'Loading…' : `${rows.length} agent${rows.length === 1 ? '' : 's'} with an open house`}
          </div>
          {!pickerOpen && (
            <button onClick={() => setPickerOpen(true)} style={GOLD_BTN}>+ New for agent</button>
          )}
        </div>

        {pickerOpen && (
          <div style={{
            background:'white',
            border:'1px solid #e8e8e8',
            borderRadius:'12px',
            padding:'20px 22px',
            marginBottom:'20px',
            display:'flex',
            flexDirection:'column',
            gap:'12px',
            maxWidth:'560px',
          }}>
            <div style={{fontSize:'10px',fontWeight:700,fontFamily:FONT_HEAD,color:'#999',letterSpacing:'1.5px'}}>NEW OPEN HOUSE FOR AGENT</div>
            <select
              value={pickerAgentId}
              onChange={e => setPickerAgentId(e.target.value)}
              style={{
                font:'inherit',
                height:'40px',
                padding:'0 12px',
                border:'1.5px solid #e8e8e8',
                borderRadius:'8px',
                background:'white',
                color:'#1d2e42',
                boxSizing:'border-box',
                outline:'none',
              }}
            >
              <option value="">— Select agent —</option>
              {agents.map(a => {
                const hasBrokerage = !!(a.brokerage && String(a.brokerage).trim())
                const baseName = a.full_name || a.email || '(no name)'
                const label = hasBrokerage
                  ? `${baseName} · ${a.brokerage}`
                  : `${baseName} — needs brokerage set`
                return (
                  <option key={a.id} value={a.id} disabled={!hasBrokerage}>{label}</option>
                )
              })}
            </select>
            <div style={{fontSize:'11px',color:'#666'}}>
              Agents without a brokerage are greyed out — set their brokerage on their profile first, otherwise the open house can't render publicly.
            </div>
            <div style={{display:'flex',gap:'10px',marginTop:'4px'}}>
              <button
                onClick={handlePick}
                disabled={!pickerAgentId}
                style={{...GOLD_BTN, opacity: pickerAgentId ? 1 : 0.45, cursor: pickerAgentId ? 'pointer' : 'not-allowed'}}
              >Open editor for agent</button>
              <button
                onClick={() => { setPickerOpen(false); setPickerAgentId('') }}
                style={QUIET_BTN}
              >Cancel</button>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{padding:'40px 0',color:'#666',fontFamily:FONT_BODY}}>Loading open houses…</div>
        ) : rows.length === 0 ? (
          <RosterEmptyState onCreate={() => setPickerOpen(true)} />
        ) : (
          <RosterList
            rows={rows}
            agentsById={agentsById}
            onEdit={(row) => {
              const agent = agentsById[row.agent_id]
              if (agent) onPickAgent(agent)
              else showToast('Cannot find the agent for this row — they may have been removed.')
            }}
          />
        )}
    </div>
  )
}

function RosterEmptyState({ onCreate }) {
  return (
    <div style={{
      padding:'48px 24px',
      textAlign:'center',
      background:'#f4f4f2',
      border:'1px dashed #e8e8e8',
      borderRadius:'12px',
      color:'#666',
      fontFamily:FONT_BODY,
    }}>
      <div style={{fontSize:'14px',fontWeight:500,color:'#1d2e42',marginBottom:'8px',fontFamily:FONT_BODY}}>No open houses yet</div>
      <div style={{fontSize:'13px',marginBottom:'18px'}}>Use “+ New for agent” above to create the first one.</div>
    </div>
  )
}

function RosterList({ rows, agentsById, onEdit }) {
  return (
    // overflowX:auto rather than hidden. Seven fr columns cannot fit a phone,
    // and with the overflow clipped the right-hand ones — Actions among them —
    // were cut off, which put Edit out of reach entirely. Same pattern LeadsTab
    // already uses for its table a few hundred lines up.
    <div style={{
      background:'white',
      border:'1px solid #e8e8e8',
      borderRadius:'12px',
      overflowX:'auto',
      overflowY:'hidden',
      WebkitOverflowScrolling:'touch',
    }}>
      {/* The header and every row share this wrapper so one scrollbar governs
          both and their columns stay in register. */}
      <div style={{minWidth: ROSTER_MIN_WIDTH}}>
      <div style={{
        display:'grid',
        gridTemplateColumns: ROSTER_COLS,
        gap:'12px',
        padding:'12px 16px',
        background:'#f4f4f2',
        borderBottom:'1px solid #e8e8e8',
        fontSize:'10px',
        fontWeight:600,
        letterSpacing:'1px',
        color:'#666',
        fontFamily:FONT_BODY,
        textTransform:'uppercase',
      }}>
        <div>Agent</div>
        <div>Brokerage</div>
        <div>Address</div>
        <div>Status</div>
        <div>Public URL</div>
        <div>Updated</div>
        <div>Actions</div>
      </div>
      {rows.map(r => (
        <RosterRow
          key={r.id}
          row={r}
          agent={agentsById[r.agent_id]}
          onEdit={() => onEdit(r)}
        />
      ))}
      </div>
    </div>
  )
}

function RosterRow({ row, agent, onEdit }) {
  const isActive = row.is_active === true
  const agentName = agent ? (agent.full_name || agent.email || '—') : '(unknown agent)'
  const brokerage = agent?.brokerage || '—'
  const slug = row.slug || ''
  const publicUrl = slug ? `truetopproducer.com/openhouse/${slug}` : ''
  const publicHref = slug ? `https://${publicUrl}` : null

  return (
    <div style={{
      display:'grid',
      gridTemplateColumns: ROSTER_COLS,
      gap:'12px',
      padding:'14px 16px',
      borderBottom:'1px solid #e8e8e8',
      fontSize:'13px',
      fontFamily:FONT_BODY,
      color:'#1d2e42',
      alignItems:'center',
    }}>
      <div style={{fontWeight:700}}>{agentName}</div>
      <div style={{color:'#666'}}>{brokerage}</div>
      <div style={{color:'#666',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
        {row.address || <span style={{color:'#999',fontStyle:'italic'}}>(blank)</span>}
      </div>
      <div>
        <span style={isActive ? PILL('#EAF3DE','#27500A') : PILL('#F4F4F2','#666')}>
          {isActive ? 'Active' : 'Draft'}
        </span>
      </div>
      <div>
        {publicHref ? (
          <a href={publicHref} target="_blank" rel="noopener noreferrer" title={publicUrl} style={{
            display:'block',
            color:'#147efb',
            textDecoration:'none',
            fontSize:'12px',
            whiteSpace:'nowrap',
            overflow:'hidden',
            textOverflow:'ellipsis',
          }}>{publicUrl}</a>
        ) : (
          <span style={{color:'#999'}}>—</span>
        )}
      </div>
      <div style={{fontSize:'12px',color:'#666'}}>{row.updated_at ? formatDateTime(row.updated_at) : '—'}</div>
      <div>
        <button onClick={onEdit} style={{
          background:'none', border:'none', padding:0,
          color:'#147efb', fontSize:'11.5px', fontWeight:600,
          fontFamily:FONT_BODY, cursor:'pointer',
        }}>Edit</button>
      </div>
    </div>
  )
}
